import {libraryThumbnailApi} from '../api/libraryThumbnailApi.ts';
import {
    BuildThumbnailPromptRequest,
    BuildThumbnailPromptResponse,
    CreateThumbnailCandidateRequest,
    ThumbnailCandidate,
} from '../types/libraryThumbnail.ts';
import {
    useBaseMutation,
    useBaseQuery,
    useInvalidateQueries,
} from './useBaseQuery.ts';
import {libraryKeys} from './useLibrary.ts';

export const libraryThumbnailKeys = {
    all: ['libraryThumbnails'] as const,
    list: (libraryId: number) =>
        [...libraryThumbnailKeys.all, 'list', libraryId] as const,
};

const ACTIVE_STATUSES = new Set(['QUEUED', 'GENERATING']);

export const useLibraryThumbnails = (libraryId: number) => {
    return useBaseQuery<ThumbnailCandidate[]>(
        libraryThumbnailKeys.list(libraryId),
        () => libraryThumbnailApi.list(libraryId),
        {
            enabled: !!libraryId,
            // 진행 중(QUEUED/GENERATING) 후보가 있으면 5초 폴링, 없으면 중단
            refetchInterval: (query) => {
                const data = query.state.data as
                    | ThumbnailCandidate[]
                    | undefined;
                return hasActiveThumbnailCandidate(data) ? 5000 : false;
            },
        }
    );
};

export const hasActiveThumbnailCandidate = (
    candidates: ThumbnailCandidate[] | undefined
): boolean => {
    if (!candidates) return false;
    return candidates.some((c) => ACTIVE_STATUSES.has(c.status));
};

export const useBuildThumbnailPrompt = (libraryId: number) => {
    return useBaseMutation<BuildThumbnailPromptRequest, BuildThumbnailPromptResponse>(
        (body) => libraryThumbnailApi.buildPrompt(libraryId, body)
    );
};

export const useCreateThumbnailCandidate = (libraryId: number) => {
    const {invalidate} = useInvalidateQueries();

    return useBaseMutation<CreateThumbnailCandidateRequest, ThumbnailCandidate>(
        (body) => libraryThumbnailApi.create(libraryId, body),
        {
            onSuccess: () => {
                invalidate(libraryThumbnailKeys.list(libraryId));
            },
        }
    );
};

export const useSelectThumbnailCandidate = (libraryId: number) => {
    const {invalidateMultiple} = useInvalidateQueries();

    return useBaseMutation<number, void>(
        (candidateId) => libraryThumbnailApi.select(libraryId, candidateId),
        {
            onSuccess: () => {
                invalidateMultiple([
                    libraryThumbnailKeys.list(libraryId),
                    libraryKeys.detail(libraryId),
                    libraryKeys.lists(),
                ]);
            },
        }
    );
};

export const useDeleteThumbnailCandidate = (libraryId: number) => {
    const {invalidate} = useInvalidateQueries();

    return useBaseMutation<number, void>(
        (candidateId) => libraryThumbnailApi.delete(libraryId, candidateId),
        {
            onSuccess: () => {
                invalidate(libraryThumbnailKeys.list(libraryId));
            },
        }
    );
};

export const useClearLibraryThumbnail = (libraryId: number) => {
    const {invalidateMultiple} = useInvalidateQueries();

    return useBaseMutation<void, void>(
        () => libraryThumbnailApi.clearThumbnail(libraryId),
        {
            onSuccess: () => {
                invalidateMultiple([
                    libraryThumbnailKeys.list(libraryId),
                    libraryKeys.detail(libraryId),
                    libraryKeys.lists(),
                ]);
            },
        }
    );
};
